(ns client-portal.client.conversations.list.data
  (:require [client-portal.client.concepts.data :as concepts.d]
            [shared.core.components.auth0.data :as auth0.d]
            [shared.core.reactive :as re]
            [shared.core.util :as util]
            [shared.core.router :as router]
            [cljs-time.core :as time]
            [shared.core.time :as t]
            [clojure.string :as s]))

(def root-path [:conversations :list])
(def data-path (conj root-path :data))
(def filters-path (conj root-path :filters))
(def set-default-start-date?-path (conj root-path :set-default-start-date?))

(def page-size 30)

(defn build-conversations-list-page-path [page]
  (conj data-path page))

(defn current-max-page [db]
  (some->> (get-in db data-path)
           (util/when-not-loading)
           (keys)
           (apply max)))

(defn conversations-list-data-has-more? [db]
  (let [list-data (get-in db data-path)
        max-page (current-max-page db)]
    (some->> (get (util/when-not-loading list-data) max-page)
             (util/when-not-loading)
             (count)
             (<= page-size))))

(re/reg-sub :conversations/list-data-has-more?- conversations-list-data-has-more?)

(defn conversations-list-loading? [db]
  (let [list-data (get-in db data-path)]
    (or (util/loading? list-data) (some-> list-data (get 0) (util/loading?)))))

(re/reg-sub :conversations/list-loading?- conversations-list-loading?)

(defn conversations-list-next-page-loading? [db]
  (let [page (current-max-page db)]
    (and (pos? page) (util/loading? (get-in db (build-conversations-list-page-path page))))))

(re/reg-sub :conversations/list-next-page-loading?- conversations-list-next-page-loading?)

(re/reg-sub
  :conversations/list-next-page-
  (fn [db]
    (some-> (current-max-page db) (inc))))

(defn query-params->filters [db]
  (let [{:keys [sdt edt fgcid lid]} (router/current-query-params db)]
    (cond-> {}
            (not (s/blank? sdt))
            (assoc :start-date-time sdt)

            (not (s/blank? edt))
            (assoc :end-date-time edt)

            (not (s/blank? fgcid))
            (assoc :franchise-group-cohort-id (util/parse-int fgcid))

            (not (s/blank? lid))
            (assoc :location-id (util/parse-int lid)))))

(defn cleaned-list-filters [db]
  (some->> (get-in db filters-path)
           (map (fn [[k v]]
                  (when (and v (or (not (string? v)) (not (s/blank? v))))
                    [k v])))
           (filter identity)
           (into {})))

(re/reg-auth0-loader
  :conversations/load-list-data
  (fn [[[_ {:keys [page]}]] _]
    (build-conversations-list-page-path (or page 0)))
  (fn [db [_ {:keys [page]} _]]
    (when (and (not (conversations-list-next-page-loading? db))
               (not (conversations-list-loading? db))
               (get-in db set-default-start-date?-path))
      (when-let [concept-id (concepts.d/current-concept-id db)]
        (let [filters (or (cleaned-list-filters db) (query-params->filters db))
              last-loaded-id (some-> (util/when-not-loading (get-in db data-path))
                                     (get (or (dec (or page 0)) 0))
                                     (last)
                                     :id)]
          {:method :get
           :url "/api/conversations"
           :query-params (merge {:concept-id concept-id
                                 :before last-loaded-id
                                 :limit page-size}
                                filters)}))))
  :transf (fn [resp] (or (some-> resp :body (util/->kebab-case-keywords))
                         ""))
  :error-transf (fn [] []))

(re/reg-sub
  :conversations/list-data-
  (fn [db]
    (let [current-list (get-in db data-path)
          cur-page (if (util/loading? current-list) 0 (current-max-page db))]
      (if (nil? current-list)
        (re/dispatch-load (conj [:conversations/load-list-data
                                 [nil {:page (or (some-> cur-page (inc)) 0)}]]))
        current-list))))

(re/reg-sub
  :conversations/list-data-flattened-
  :<- [:conversations/list-data-]
  (fn [list-data]
    (some->> (util/when-not-loading list-data)
             (filterv #(not (util/loading? (last %))))
             (mapv last)
             (flatten))))

(re/reg-sub
  :conversations/list-last-selected-id-
  (fn [db]
    (get-in db (conj root-path :last-selected-id))))

(re/reg-event-db
  :conversations/open re/middleware
  (fn [db [conv-id]]
    (re/dispatch [:router/go-with-merged-query-params {:conv-id conv-id}])
    (assoc-in db (conj root-path :last-selected-id) conv-id)))

(re/reg-event-db
  :conversations/close re/middleware
  (fn [db]
    (re/dispatch [:router/go-with-merged-query-params {:conv-id nil}])
    db))

(defn conversations-filter-values-as-query-params [db]
  (let [{:keys [start-date-time end-date-time franchise-group-cohort-id location-id]} (get-in db filters-path)]
    (->> {:sdt start-date-time
          :edt end-date-time
          :fgcid franchise-group-cohort-id
          :lid location-id}
         (map (fn [[k v]] [k (when (and v (or (not (string? v))
                                              (not (s/blank? v))))
                               (str v))]))
         (into {}))))

(re/reg-event-db
  :conversations/apply-list-filters re/middleware
  (fn [db]
    (re/dispatch [:router/go-with-merged-query-params (conversations-filter-values-as-query-params db)])
    (assoc-in db data-path nil)))

(re/reg-sub :conversations/filter-values-as-query-params- conversations-filter-values-as-query-params)

(re/reg-event-fx
  :conversations/initialize re/middleware
  (fn [_]
    {:forward-events {:register :conversations-initialization-listener
                      :events #{:concepts/select}
                      :dispatch-to [:conversations/clear-on-change]}}))

(re/reg-event-db
  :conversations/clear-on-change re/middleware
  (fn [db]
    (let [initial-concept-set-path (conj root-path :initial-concept-set?)]
      (if (get-in db initial-concept-set-path)
        (do
          (re/dispatch [:conversations/clear-set-default-start-date])
          (re/dispatch [:router/go-with-merged-query-params
                        {:sdt nil
                         :edt nil
                         :fgcid nil
                         :lid nil}
                        {:on-route-change #(re/dispatch [:conversations/set-default-start-date])}])
          (-> db
              (assoc-in data-path nil)
              (assoc-in filters-path {})))
        (assoc-in db initial-concept-set-path true)))))

(re/reg-event-db
  :conversations/set-initial-filters re/middleware
  (fn [db]
    (let [{:keys [sdt edt fgcid lid] :as qps} (router/current-query-params db)
          cur-filters (get-in db filters-path)]
      (if (and (empty? cur-filters) (not-empty qps))
        (-> db
            (assoc-in data-path nil)
            (assoc-in filters-path (cond-> {}
                                           (not (s/blank? sdt)) (assoc :start-date-time sdt)
                                           (not (s/blank? edt)) (assoc :end-date-time edt)
                                           (not (s/blank? fgcid)) (assoc :franchise-group-cohort-id (util/parse-int fgcid))
                                           (not (s/blank? lid)) (assoc :location-id (util/parse-int lid)))))
        db))))

(re/reg-event-db
  :conversations/open-input-conversation-id re/middleware
  (fn [db [{:keys [conversation-id]}]]
    (let [conversation-id (or conversation-id (get-in db (conj root-path :conversation-id)))]
      (when (util/string-is-uuid? conversation-id)
        (re/dispatch [:conversations/open conversation-id]))
      (update-in db root-path dissoc :conversation-id))))

(re/reg-event-db
  :conversations/list-go-with-params re/middleware
  (fn [db]
    (re/dispatch [:router/go [:conversations/list nil (->> (merge (conversations-filter-values-as-query-params db)
                                                                  {:cid (some-> (concepts.d/current-concept-id db)
                                                                                (str))})
                                                           (filter (fn [[_ v]] (not-empty v)))
                                                           (into {}))]])
    db))

(re/reg-event-db
  :conversations/set-default-start-date re/middleware
  (fn [db]
    (let [one-week-ago (time/minus (time/now) (time/days 7))]
      (if (and (empty? (query-params->filters db))
               (empty? (get-in db filters-path))
               (not (get-in db set-default-start-date?-path)))
        (let [sdt (t/as-format one-week-ago "yyyy-MM-dd'T'HH':00:00.000Z'")]
          (re/dispatch [:router/go-with-merged-query-params
                        {:sdt sdt}
                        {:on-route-change #(re/dispatch [:conversations/set-initial-filters])}])
          (-> db
              (assoc-in set-default-start-date?-path true)
              (assoc-in (conj filters-path :start-date-time) sdt)))
        (assoc-in db set-default-start-date?-path true)))))

(re/reg-event-db
  :conversations/clear-set-default-start-date re/middleware
  (fn [db]
    (update-in db root-path dissoc :set-default-start-date?)))
